import { useEffect, useState } from 'react';
import { Card, EmptyState, FormSelect } from '@/components/ui';
import { superApi, type VoiceAgentStats } from '@/shared/api/superApi';

/**
 * Voice v3 F11 — טאב "סטטיסטיקות" בסטודיו הסוכן: KPIs (נפח, משך, ציון QA),
 * ברים יומיים, התפלגות resolution ומשפך נטישה על הזרימה (איפה שיחות מתות).
 */

const RESOLUTION_LABEL: Record<string, string> = {
  resolved: 'טופל בשיחה',
  follow_up: 'דורש המשך טיפול',
  unresolved: 'לא נפתר',
};

const STATUS_LABEL: Record<string, string> = {
  ended: 'הסתיימו',
  connected: 'פעילות',
  failed: 'נכשלו',
  missed: 'לא נענו',
  after_hours: 'מחוץ לשעות',
  pending: 'ממתינות',
  ringing: 'מצלצלות',
};

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--ok, #22c55e)';
  if (score >= 60) return 'var(--warn, #eab308)';
  return 'var(--danger, #ef4444)';
}

export function StudioStatsTab({ agentId }: { agentId: number }) {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<VoiceAgentStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void superApi.voiceAgentStats(agentId, days)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [agentId, days]);

  if (loading) return <p style={{ color: 'var(--muted)', padding: 'var(--gap)' }}>טוען סטטיסטיקות…</p>;
  if (!stats) return <EmptyState icon="📊" title="אין נתונים" description="טעינת הסטטיסטיקות נכשלה." />;

  const perDay = Object.entries(stats.per_day);
  const maxDay = Math.max(1, ...perDay.map(([, c]) => c));
  const resolutionsTotal = Object.values(stats.resolutions ?? {}).reduce((a, b) => a + b, 0);

  return (
    <div className="stack">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <FormSelect value={String(days)} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 140 }}>
          <option value="7">7 ימים</option>
          <option value="30">30 ימים</option>
          <option value="90">90 ימים</option>
        </FormSelect>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--gap)' }}>
        <Kpi label="סה״כ שיחות" value={String(stats.total_calls)} />
        <Kpi label="משך ממוצע" value={stats.avg_duration_sec ? `${Math.round(stats.avg_duration_sec)} שנ׳` : '—'} />
        <Kpi
          label={`ציון QA ממוצע (${stats.scored_calls} שיחות)`}
          value={stats.avg_score != null ? String(stats.avg_score) : '—'}
          valueColor={stats.avg_score != null ? scoreColor(stats.avg_score) : undefined}
        />
        <Kpi label="מחוץ לשעות" value={String(stats.by_status?.after_hours ?? 0)} />
      </div>

      {/* ברים יומיים */}
      <Card title="שיחות לפי יום">
        {perDay.length === 0 ? (
          <p style={{ color: 'var(--muted)', margin: 0 }}>אין שיחות בתקופה.</p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, overflowX: 'auto', paddingTop: 8 }}>
            {perDay.map(([d, c]) => (
              <div key={d} title={`${d}: ${c} שיחות`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 26 }}>
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{c}</span>
                <div style={{
                  width: 18, borderRadius: '4px 4px 0 0',
                  height: Math.max(6, Math.round((c / maxDay) * 80)),
                  background: 'color-mix(in oklch, var(--accent) 65%, var(--surface))',
                }} />
                <span style={{ fontSize: 10, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{d.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--gap)' }}>
        {/* סטטוסים */}
        <Card title="לפי סטטוס">
          <div className="stack" style={{ gap: 6 }}>
            {Object.entries(stats.by_status ?? {}).map(([s, c]) => (
              <div key={s} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--t-sm)' }}>
                <span>{STATUS_LABEL[s] ?? s}</span>
                <strong>{c}</strong>
              </div>
            ))}
          </div>
        </Card>

        {/* resolutions */}
        <Card title="תוצאות שיחה (QA)">
          {resolutionsTotal === 0 ? (
            <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>אין עדיין שיחות מנותחות. הציון נוצר אוטומטית אחרי כל שיחה עם תמלול.</p>
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {Object.entries(stats.resolutions).map(([r, c]) => (
                <div key={r}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--t-sm)', marginBottom: 3 }}>
                    <span>{RESOLUTION_LABEL[r] ?? r}</span>
                    <strong>{c} ({Math.round((c / resolutionsTotal) * 100)}%)</strong>
                  </div>
                  <div style={{ height: 6, borderRadius: 999, background: 'var(--line)' }}>
                    <div style={{
                      height: '100%', borderRadius: 999, width: `${(c / resolutionsTotal) * 100}%`,
                      background: r === 'resolved' ? 'var(--ok, #22c55e)' : r === 'follow_up' ? 'var(--warn, #eab308)' : 'var(--danger, #ef4444)',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* משפך נטישה */}
      <Card title="משפך נטישה בזרימה">
        {!stats.funnel ? (
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>אין זרימת שיחה מחוברת לסוכן — המשפך זמין רק עם זרימה.</p>
        ) : (
          <div className="stack" style={{ gap: 6 }}>
            <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              {stats.funnel.completed} שיחות הגיעו לשלב סיום/העברה.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: 'var(--t-sm)' }}>
                <thead>
                  <tr><th>שלב</th><th>נכנסו</th><th>ננטשו כאן</th><th>אחוז נטישה</th></tr>
                </thead>
                <tbody>
                  {stats.funnel.stages.map((s) => {
                    const rate = s.entered > 0 ? Math.round((s.dropped_here / s.entered) * 100) : 0;
                    return (
                      <tr key={s.stage_key}>
                        <td>{s.name || s.stage_key}</td>
                        <td>{s.entered}</td>
                        <td>{s.dropped_here}</td>
                        <td style={{ color: rate >= 30 ? 'var(--danger, #ef4444)' : rate >= 10 ? 'var(--warn, #eab308)' : 'inherit', fontWeight: rate >= 30 ? 700 : 400 }}>
                          {s.entered > 0 ? `${rate}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <Card>
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: valueColor }}>{value}</div>
    </Card>
  );
}
